import React, { useState, useEffect } from 'react';
import './PayerIdentityChoice.css';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

type ContactMode = 'anonymous' | 'telegram' | 'email';

interface PayerIdentityChoiceProps {
  orderId: string;
  onIdentityChange?: (isAnonymous: boolean, telegramUser?: TelegramUser) => void;
}

const TelegramLoginWidget: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [botUsername, setBotUsername] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/auth/config', { credentials: 'include' })
      .then(r => r.json())
      .then(cfg => cfg.telegramBotUsername && setBotUsername(cfg.telegramBotUsername))
      .catch(() => setBotUsername((window as any).TELEGRAM_BOT_USERNAME || 'plzpaymebot'));
  }, []);

  useEffect(() => {
    if (!botUsername) return;
    const container = containerRef.current;
    if (!container) return;

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');

    container.appendChild(script);

    return () => {
      if (container.contains(script)) container.removeChild(script);
    };
  }, [botUsername]);

  return <div ref={containerRef} />;
};

export const PayerIdentityChoice: React.FC<PayerIdentityChoiceProps> = ({
  orderId,
  onIdentityChange,
}) => {
  const [mode, setMode] = useState<ContactMode>('anonymous');
  const [telegramUser, setTelegramUser] = useState<TelegramUser | null>(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactSaved, setContactSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const savePayerInfo = async (data: Record<string, unknown>) => {
    try {
      await fetch(`/api/orders/${orderId}/payer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    } catch (err) {
      console.error('Failed to save payer info:', err);
    }
  };

  // Telegram Login Widget callback
  useEffect(() => {
    (window as any).onTelegramAuth = (user: TelegramUser) => {
      setTelegramUser(user);

      savePayerInfo({
        payerTelegramId: user.id.toString(),
        payerUsername: user.username,
        payerFirstName: user.first_name,
        payerPhotoUrl: user.photo_url,
        isAnonymous: false,
      });

      if (onIdentityChange) onIdentityChange(false, user);
    };

    return () => { delete (window as any).onTelegramAuth; };
  }, [orderId, onIdentityChange]);

  const handleModeChange = (newMode: ContactMode) => {
    setMode(newMode);
    setTelegramUser(null);
    setContactSaved(false);

    if (newMode === 'anonymous') {
      savePayerInfo({ isAnonymous: true });
      if (onIdentityChange) onIdentityChange(true);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email && !phone) return;
    setSaving(true);
    await savePayerInfo({
      isAnonymous: false,
      payerEmail: email || undefined,
      payerPhone: phone || undefined,
    });
    setSaving(false);
    setContactSaved(true);
    if (onIdentityChange) onIdentityChange(false);
  };

  return (
    <div className="payer-identity-choice">
      <div className="identity-label">
        <span className="identity-icon">👤</span>
        <span>Identify yourself to the merchant?</span>
      </div>

      <div className="identity-options">
        <button
          type="button"
          className={`identity-opt ${mode === 'telegram' ? 'active' : ''}`}
          onClick={() => handleModeChange('telegram')}
        >
          <span className="opt-icon">💬</span>
          <span className="opt-text">
            <strong>Telegram</strong>
            <span className="opt-hint">The merchant can message you</span>
          </span>
          {mode === 'telegram' && <span className="opt-check">✓</span>}
        </button>

        <button
          type="button"
          className={`identity-opt ${mode === 'email' ? 'active' : ''}`}
          onClick={() => handleModeChange('email')}
        >
          <span className="opt-icon">✉️</span>
          <span className="opt-text">
            <strong>Email / Phone</strong>
            <span className="opt-hint">Leave contact details</span>
          </span>
          {mode === 'email' && <span className="opt-check">✓</span>}
        </button>

        <button
          type="button"
          className={`identity-opt ${mode === 'anonymous' ? 'active anon' : ''}`}
          onClick={() => handleModeChange('anonymous')}
        >
          <span className="opt-icon">🔒</span>
          <span className="opt-text">
            <strong>Stay anonymous</strong>
            <span className="opt-hint">The merchant won't know who paid</span>
          </span>
          {mode === 'anonymous' && <span className="opt-check">✓</span>}
        </button>
      </div>

      {mode === 'telegram' && (
        <div className="identity-login-area">
          {telegramUser ? (
            <div className="telegram-user-card">
              {telegramUser.photo_url && (
                <img
                  src={telegramUser.photo_url}
                  alt={telegramUser.first_name}
                  className="user-avatar"
                />
              )}
              <div className="user-info">
                <div className="user-name">
                  {telegramUser.first_name} {telegramUser.last_name}
                </div>
                {telegramUser.username && (
                  <div className="user-username">@{telegramUser.username}</div>
                )}
              </div>
              <button
                className="change-user-btn"
                onClick={() => setTelegramUser(null)}
                type="button"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="telegram-login-widget">
              <p className="login-hint">Sign in with Telegram — the merchant will be able to message you:</p>
              <TelegramLoginWidget />
            </div>
          )}
        </div>
      )}

      {mode === 'email' && (
        <div className="identity-login-area">
          {contactSaved ? (
            <div className="contact-saved">
              ✓ Contact saved
              <button
                className="change-user-btn"
                type="button"
                onClick={() => setContactSaved(false)}
              >
                Change
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <input
                type="email"
                placeholder="Email (optional)"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="contact-input"
                autoComplete="email"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className="contact-input"
                autoComplete="tel"
              />
              <button
                type="submit"
                className="contact-submit"
                disabled={saving || (!email && !phone)}
              >
                {saving ? 'Saving…' : 'Save contact'}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};
